import { gql } from '@apollo/client';
import paginated from '../client/pagination';
import { createStringVariables } from './utils';
import { BENEFIT_SCHEDULE_FIELDS } from './benefitItem';
const FIELDS = `
annual_limit
code
cover_image
description
documents
extra_fields
full_name
full_name_en
full_name_zh
headline
icon
is_insurance_plan
lifetime_limit
max_issue_age_unit
max_issue_age_value
max_renewal_age
min_issue_age_unit
min_issue_age_value
plan_details_url
plan_option
product_risk_url
product_type
product_type_cover_image
provision_url
renewal_interval
renewal_period
short_name
short_name_en
short_name_zh
smm_annual_limit
supplementary_products
triannual_limit
type
valid_date_of_birth_from
valid_date_of_birth_to
vhis_certification_number
vhis_type
waiting_period
allow_flexible_selection
attribute_values
require_sum_assured
`;
export const RETRIEVE_PRODUCT = gql`
  query Product($id: ID!) {
    product(id: $id) @rest(type: "Product", path: "/product/{args.id}") {
      __typename
      ${FIELDS}
    }
  }
`;
const listProductVariables = createStringVariables(['type', 'code', 'from_product', 'from_bundle_type', 'to_product', 'to_bundle_type', 'tenant_code']);
export const LIST_PRODUCT = gql`
  query Products(
    ${listProductVariables.variableTypes}
  ) {
    products(
      ${listProductVariables.variables}
    )
      @rest(type: "Product", path: "/product?{args}",) {
      __typename
      ${FIELDS}
    }
  }
`;
export const LIST_PARTNER_PRODUCT = gql`
  query PartnerProducts($application_case_id: ID!) {
    products(application_case_id: $application_case_id)
      @rest(type: "[Product]", path: "/outpatient/product?application_case_id={args.application_case_id}") {
      __typename
      ${FIELDS}
    }
  }
`;
export const LIST_ELIGIBILITIES_OF_PRODUCT = gql`
  query EligibilitiesOfProduct($product_id: ID!, $type_code: String) {
    eligibilities(product_id: $product_id, type_code: $type_code)
      @rest(
        type: "[Eligibility]"
        path: "/product/{args.product_id}/eligibility?{args}"
      ) {
      __typename
      product
      code
      group_name
      group_name_en
      group_name_zh
      min_issue_age_value
      min_issue_age_unit
      max_issue_age_value
      max_issue_age_unit
      valid_date_of_birth_from
      valid_date_of_birth_to
    }
  }
`;
export const RETRIEVE_PRODUCT_BENEFIT = gql`
  query BenefitSchedule($id: ID!, $evaluation_date: String) {
    benefitSchedule(id: $id, evaluation_date: $evaluation_date)
      @rest(
        type: "BenefitSchedule"
        path: "/product/{args.id}/benefit?{args}"
      ) {
      __typename
      ${BENEFIT_SCHEDULE_FIELDS}
    }
  }
`;
const listBenefitScheduleVariables = createStringVariables(['product_code', 'latest_of_product_at']);
export const LIST_BENEFIT_SCHEDULES = paginated(gql`
  query BenefitSchedules(
    ${listBenefitScheduleVariables.variableTypes}
  ) {
    benefitSchedules(
      ${listBenefitScheduleVariables.variables}
    )
      @rest(
        type: "BenefitSchedule"
        path: "/benefit/schedule?{args}"
        method: "GET"
      ) {
      __typename
      ${BENEFIT_SCHEDULE_FIELDS}
    }
  }
`);
export const LIST_SUM_ASSURED_RANGE_OF_PRODUCT = gql`
  query SumAssuredRanges($product_id: ID!) {
    sumAssuredRanges(product_id: $product_id)
      @rest(
        type: "SumAssuredRange"
        path: "/product/{args.product_id}/sum-assured-ranges"
      ) {
      __typename
      product
      min_issue_age
      max_issue_age
      min_sum_assured
      max_sum_assured
      create_date
    }
  }
`;
const listAppCaseProductOptionsStringVariables = createStringVariables(['type', 'from_product']);
export const LIST_APP_CASE_PRODUCT_OPTIONS = gql`
  query AppCaseProducts(
    $application_case_id: ID!
    ${listAppCaseProductOptionsStringVariables.variableTypes}
  ) {
    products(
      application_case_id: $application_case_id
      ${listAppCaseProductOptionsStringVariables.variables}
    )
      @rest(
        type: "ProductOption"
        path: "/application-case/{args.application_case_id}/product-option?{args}"
      ) {
      __typename
      type
      from_product
      quotation
      to_product
    }
  }
`;
export const LIST_POLICY_PRODUCT_OPTIONS = gql`
  query PolicyProductOptions($id: ID!, $type: String) {
    products(id: $id, type: $type)
      @rest(
        type: "PolicyProductOption"
        path: "/policy/{args.id}/product-option?{args}"
      ) {
      __typename
      type
      from_product
      quotation
      to_product
      eligibilities
      is_eligible
    }
  }
`;
const listProductReleaseContentsVariables = createStringVariables(['product_code', 'language_code', 'ordering']);
export const LIST_PRODUCT_RELEASE_CONTENTS = paginated(gql`
  query ProductReleaseContents(
    ${listProductReleaseContentsVariables.variableTypes}
  ) {
    productReleaseContents(
      ${listProductReleaseContentsVariables.variables}
    )
      @rest(
        type: "ProductReleaseContent"
        path: "/product/release-content?{args}"
      ) {
      __typename
      id
      language
      subject
      body
      url
      effective_date
    }
  }
`);
const listProductGroupVariables = createStringVariables(['type', 'code', 'from_bundle_type', 'variable_attribute']);
export const LIST_PRODUCT_GROUP = gql`
  query ListProductGroup(
    ${listProductGroupVariables.variableTypes}
  ) {
    productGroups(
      ${listProductGroupVariables.variables}
    ) @rest(type: "[ProductGroup]", path: "/product/group?{args}") {
      __typename
      elements
      group_ids
      variables
    }
  }
`;
const listProductOptionsVariables = createStringVariables(['from_product', 'to_product', 'type']);
export const LIST_PRODUCT_OPTIONS = gql`
  query ProductOptions(${listProductOptionsVariables.variableTypes}) {
    productOptions(${listProductOptionsVariables.variables})
      @rest(type: "[ProductOption]", path: "/product/option?{args}") {
      __typename
      from_product
      to_product
      type
    }
  }
`;
const PRODUCT_DOCUMENT_FIELDS = `
id
product_codes
category
type
name
name_zh
name_en
file
file_zh
file_en
effective_date
platforms
expiry_date
purchase_date_from
purchase_date_to
create_date
update_date
product_code
`;
const listCustomerProductDocumentVariables = createStringVariables(['product_code', 'product_type', 'category', 'type', 'evaluate_at', 'effective_date__gte', 'effective_date__lte', 'expiry_date__gte', 'expiry_date__lte', 'ordering']);
export const LIST_PRODUCT_DOCUMENT = gql`
  query CustomerProductDocuments(${listCustomerProductDocumentVariables.variableTypes}) {
    customerProductDocuments(${listCustomerProductDocumentVariables.variables})
      @rest(type: "[ProductDocument]", path: "/product-document?{args}") {
      __typename
      ${PRODUCT_DOCUMENT_FIELDS}
    }
  }
`;
export const RETRIEVE_PRODUCT_DOCUMENT = gql`
  query CustomerProductDocument($id: ID!) {
    customerProductDocument(id: $id)
      @rest(type: "ProductDocument", path: "/product-document/{args.id}") {
      __typename
      ${PRODUCT_DOCUMENT_FIELDS}
    }
  }
`;